// client/src/components/company/BankManagement.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Landmark, Clock, X, RefreshCw, UserPlus, Trash2, Shield, User } from 'lucide-react';
import BankSearchInput from '../common/BankSearchInput';
import '../../styles/BankSearchInput.css';
import '../../styles/BankManagement.css';

const BankManagement = () => {
  const { t } = useTranslation();

  const [bankReferrals, setBankReferrals] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState('member');
  const [canManageBanks, setCanManageBanks] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    bankName: '',
    language: 'it'  // ✅ NEW: Default language
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  // Fix: Usa useCallback per fetchBankData per evitare il warning
  const fetchBankData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/company/bank-referrals');
      setBankReferrals(response.data.bankReferrals);
      setPendingInvites(response.data.pendingInvites);
      setCurrentUserRole(response.data.currentUserRole || 'member');
      setCanManageBanks(response.data.canManageBanks || false);
      
    } catch (err) {
      setError(err.response?.data?.message || t('bankManagement.errors.fetchFailed'));
      console.error('Error fetching bank data:', err);
    } finally {
      setLoading(false);
    }
  }, [t]); // Aggiungi t come dipendenza

  useEffect(() => {
    fetchBankData();
  }, [fetchBankData]); // Ora fetchBankData è nella dipendenza

  // Raggruppa referenti e inviti pendenti per banca (chiave: nome banca, case-insensitive)
  const banks = useMemo(() => {
    const map = new Map();
    const add = (name) => {
      const label = (name || '').trim() || t('bankManagement.noBankSpecified');
      const key = label.toLowerCase();
      if (!map.has(key)) map.set(key, { name: label, users: [], invites: [] });
      return map.get(key);
    };
    bankReferrals.forEach(r => add(r.referralDetails?.bankName).users.push(r));
    pendingInvites.forEach(i => add(i.bankName).invites.push(i));
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [bankReferrals, pendingInvites, t]);

  // Enhanced error handling section in BankManagement.js

const handleInviteSubmit = async (e) => {
  e.preventDefault();
  setInviteLoading(true);
  setError('');
  setSuccess('');

  try {
    const response = await axios.post('/api/company/invite-bank-referral', inviteForm);
    
    if (response.data.type === 'invitation_sent') {
      setSuccess(t('bankManagement.inviteSent') || 'Bank referral invitation sent successfully');
    } else if (response.data.type === 'existing_user_affiliated') {
      setSuccess(
        t('bankManagement.existingUserAffiliated', {
          bankName: response.data.bankUser.bankName
        })
      );
    } else {
      setSuccess(response.data.message || t('bankManagement.inviteSent'));
    }
    
    setInviteForm({ email: '', firstName: '', lastName: '', bankName: '', language: 'it'  });
    setShowInviteForm(false);
    fetchBankData();
    
  } catch (err) {
    const errorCode = err.response?.data?.errorCode;
    const errorMessage = err.response?.data?.message;
    const existingBankName = err.response?.data?.existingBankName;
    
    // ✅ CHIUDI IL POPUP PRIMA DI MOSTRARE L'ERRORE
    setShowInviteForm(false);
    
    let displayError;
    
    // Handle specific error codes
    switch (errorCode) {
      case 'bankUserBankMismatch':
        displayError = t('bankManagement.errors.bankUserBankMismatch', { 
          existingBank: existingBankName 
        }) || `This bank user is already registered with ${existingBankName}. Cannot link with a different bank.`;
        break;
      case 'bankAlreadyAffiliated':
        displayError = t('bankManagement.errors.bankAlreadyAffiliated') || 'This bank is already linked with your company';
        break;
      case 'userExistsNotBank':
        displayError = t('bankManagement.errors.userExistsNotBank') || 'A user with this email already exists but is not a bank user';
        break;
      case 'invitationPending':
        displayError = t('bankManagement.errors.invitationPending') || 'An invitation for this email is already pending from your company';
        break;
      case 'validEmailRequired':
        displayError = t('bankManagement.errors.validEmailRequired') || 'Valid email is required';
        break;
      case 'firstNameRequired':
        displayError = t('bankManagement.errors.firstNameRequired') || 'First name is required';
        break;
      case 'lastNameRequired':
        displayError = t('bankManagement.errors.lastNameRequired') || 'Last name is required';
        break;
      case 'bankNameRequired':
        displayError = t('bankManagement.errors.bankNameRequired') || 'Bank name is required';
        break;
      case 'emailSendFailed':
        displayError = t('bankManagement.errors.emailSendFailed') || 'Failed to send invitation email. Please try again.';
        break;
      case 'genericInviteError':
        displayError = t('bankManagement.errors.genericInviteError') || 'Failed to send invitation. Please try again.';
        break;
      default:
        // Fallback to original message or generic error
        displayError = errorMessage || t('bankManagement.errors.inviteFailed') || 'Failed to send invitation';
    }
    
    setError(displayError);
  } finally {
    setInviteLoading(false);
  }
};

  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(`/api/company/bank-referral-invite/${inviteId}/resend`);
      setSuccess(t('bankManagement.inviteResent'));
      fetchBankData();
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = errorCode 
        ? t(`bankManagement.errors.${errorCode}`)
        : err.response?.data?.message || t('bankManagement.errors.ResendFailed');
      setError(errorMessage);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (window.confirm(t('bankManagement.confirmCancelInvite'))) {
      try {
        await axios.delete(`/api/company/bank-referral-invite/${inviteId}`);
        setSuccess(t('bankManagement.inviteCancelled'));
        fetchBankData();
      } catch (err) {
        const errorCode = err.response?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`bankManagement.errors.${errorCode}`)
          : err.response?.data?.message || t('bankManagement.errors.cancelFailed');
        setError(errorMessage);
      }
    }
  };

  const handleRemoveReferral = async (userId, userName) => {
    if (window.confirm(t('bankManagement.confirmRemoveReferral', { name: userName }))) {
      try {
        await axios.delete(`/api/company/bank-referral/${userId}`);
        setSuccess(t('bankManagement.referralRemoved'));
        fetchBankData();
      } catch (err) {
        const errorCode = err.response?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`bankManagement.errors.${errorCode}`)
          : err.response?.data?.message || t('bankManagement.errors.removeFailed');
        setError(errorMessage);
      }
    }
  };

  const resetInviteForm = () => {
    setInviteForm({ email: '', firstName: '', lastName: '', bankName: '', language: 'it' });
    setShowInviteForm(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="company-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-dashboard">
      <div className="card">
        {/* Card Header */}
        <div className="card-title">
          <Landmark className="card-title-icon" size={20} />
          <span>{t('bankManagement.title')}</span>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span className={`role-badge ${currentUserRole}`}>
            {currentUserRole === 'owner' && <Shield size={12} />}
            {currentUserRole === 'member' && <User size={12} />}
            {currentUserRole === 'owner' ? t('bankManagement.organizationOwner') : t('bankManagement.teamMember')}
          </span>
          <button className="btn btn-primary" onClick={() => setShowInviteForm(true)}>
            <UserPlus size={18} />
            {t('bankManagement.inviteReferral')}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible">
            {error}
            <button className="close" onClick={() => setError('')}>&times;</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-dismissible">
            {success}
            <button className="close" onClick={() => setSuccess('')}>&times;</button>
          </div>
        )}

      <div className="team-section">
        <h2 style={{ marginBottom: '4px' }}>
          {t('bankManagement.activeBanks')}
          <span className="count-badge">{banks.length}</span>
        </h2>
        <p className="bank-section-subtitle">
          {t('bankManagement.totalReferrals', { count: bankReferrals.length })} · {' '}
          {t('bankManagement.pendingInvitesCount', { count: pendingInvites.length })}
        </p>

        {banks.length === 0 ? (
          <div className="empty-state">
            <Landmark size={48} className="empty-icon" />
            <h3>{t('bankManagement.noReferrals')}</h3>
            <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowInviteForm(true)}>
              <UserPlus size={18} />
              {t('bankManagement.inviteFirstReferral') || 'Invita il primo referente'}
            </button>
          </div>
        ) : (
          <div className="bank-grid">
            {banks.map((bank) => (
              <div key={bank.name} className="bank-card">
                <div className="bank-card-header">
                  <div className="bank-card-heading">
                    <h3 className="bank-card-name">{bank.name}</h3>
                    <span className="bank-card-count">
                      {t('bankManagement.referralsCount', { count: bank.users.length })}
                    </span>
                  </div>
                </div>

                <div className="bank-user-list">
                  {bank.users.map((referral) => (
                    <div key={referral._id} className="bank-user-row">
                      <div className="bank-user-avatar">
                        {referral.firstName?.[0]}{referral.lastName?.[0]}
                      </div>
                      <div className="bank-user-details">
                        <div className="bank-user-name">{referral.firstName} {referral.lastName}</div>
                        <div className="bank-user-email">{referral.email}</div>
                      </div>
                      {canManageBanks && (
                        <div className="bank-user-actions">
                          <button
                            className="bank-icon-btn danger"
                            onClick={() => handleRemoveReferral(
                              referral._id,
                              `${referral.firstName} ${referral.lastName}`
                            )}
                            title={t('bankManagement.removeReferral')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {bank.invites.map((invite) => (
                    <div key={invite._id} className="bank-user-row pending">
                      <div className="bank-user-avatar pending">
                        {invite.firstName?.[0]}{invite.lastName?.[0]}
                      </div>
                      <div className="bank-user-details">
                        <div className="bank-user-name">{invite.firstName} {invite.lastName}</div>
                        <div className="bank-user-email">{invite.email}</div>
                        <span
                          className="bank-pending-badge"
                          title={`${t('bankManagement.invitedBy')}: ${invite.invitedBy?.firstName || ''} ${invite.invitedBy?.lastName || ''}`}
                        >
                          <Clock size={11} />
                          {t('bankManagement.invitePending')} · {t('bankManagement.expires')}{' '}
                          {new Date(invite.expiresAt).toLocaleDateString()}
                        </span>
                      </div>
                      {canManageBanks && (
                        <div className="bank-user-actions">
                          <button
                            className="bank-icon-btn primary"
                            onClick={() => handleResendInvite(invite._id)}
                            title={t('bankManagement.resendInvite')}
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            className="bank-icon-btn danger"
                            onClick={() => handleCancelInvite(invite._id)}
                            title={t('bankManagement.cancelInvite')}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

    {showInviteForm && createPortal(
      <div className="modal-overlay" onClick={resetInviteForm}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{t('bankManagement.inviteNewReferral')}</h3>
            <button className="close-btn" onClick={resetInviteForm}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleInviteSubmit}>
            <div className="form-group">
              <label>{t('bankManagement.email')} *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                placeholder={t('bankManagement.emailPlaceholder')}
                disabled={inviteLoading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('bankManagement.firstName')} *</label>
                <input
                  type="text"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  required
                  placeholder={t('bankManagement.firstNamePlaceholder')}
                  disabled={inviteLoading}
                />
              </div>
              <div className="form-group">
                <label>{t('bankManagement.lastName')} *</label>
                <input
                  type="text"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  required
                  placeholder={t('bankManagement.lastNamePlaceholder')}
                  disabled={inviteLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('bankManagement.bankName')} *</label>
              <BankSearchInput
                value={inviteForm.bankName}
                onChange={(value) => setInviteForm({ ...inviteForm, bankName: value })}
                placeholder={t('bankManagement.searchBankPlaceholder') || 'Cerca e seleziona una banca...'}
                required
                disabled={inviteLoading}
              />
              {inviteForm.bankName && (
                <small className="form-text text-muted">
                  Banca selezionata: <strong>{inviteForm.bankName}</strong>
                </small>
              )}

              {/* Support */}
              <div style={{
                marginTop: '10px',
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#6c757d'
              }}>
                {t('bankManagement.bankNotFound')}{' '}
                <a
                  href="mailto:support@euracle.it"
                  style={{ color: '#1a73e8', textDecoration: 'none' }}
                >
                  {t('bankManagement.supportEmail')}
                </a>
              </div>

              <div className="form-group" style={{ marginTop: '24px' }}>
                <label>{t('bankManagement.inviteLanguage') || 'Lingua Invito'} *</label>
                <select
                  value={inviteForm.language}
                  onChange={(e) => setInviteForm({ ...inviteForm, language: e.target.value })}
                  required
                  disabled={inviteLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: inviteLoading ? '#f5f5f5' : 'white'
                  }}
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
                <small className="form-text text-muted">
                  {t('bankManagement.languageHelp') || 'Le email di notifica verranno inviate nella lingua selezionata'}
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={resetInviteForm} disabled={inviteLoading}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={inviteLoading || !inviteForm.bankName}>
                {inviteLoading ? (
                  <>
                    <div className="spinner-sm"></div>
                    {t('common.sending')}
                  </>
                ) : (
                  t('bankManagement.sendInvite')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    , document.body)}
  </div>
  );
};

export default BankManagement;